import { Box, Grid, Group, NumberInput, Paper, SegmentedControl, Select, Text, ThemeIcon } from "@mantine/core";
import { IconBrush, IconClick, IconLayout, IconLayoutSidebar, IconPalette, IconSettings, IconSquareRoundedLetterN } from "@tabler/icons-react";
import { ColorPickerRow } from "./color-picker-row";

interface TokenValues {
  layoutStyle?: "compact" | "wide";
  fontFamily?: string;
  fontSize?: number;
  borderRadius?: number;
  spacing?: number;
  headerHeight?: number;
  navbarWidth?: number;
  primaryColor?: string;
  headerBg?: string;
  headerText?: string;
  headerBorder?: string;
  navbarBg?: string;
  navbarText?: string;
  navbarActiveBg?: string;
  navbarActiveText?: string;
  buttonBg?: string;
  buttonText?: string;
  buttonHoverBg?: string;
  bodyBg?: string;
  cardBg?: string;
  cardBorder?: string;
  linkColor?: string;
  successColor?: string;
  warningColor?: string;
  errorColor?: string;
}

interface Props {
  values: TokenValues;
  onChange: (key: string, value: any) => void;
  suggestedColors?: string[];
}

const fontFamilyOptions = [
  { value: "Inter, sans-serif", label: "Inter" },
  { value: "'Segoe UI', sans-serif", label: "Segoe UI" },
  { value: "Roboto, sans-serif", label: "Roboto" },
  { value: "'Open Sans', sans-serif", label: "Open Sans" },
  { value: "system-ui, sans-serif", label: "System UI" },
];

function SectionHeader({ icon, label, description }: { icon: React.ReactNode; label: string; description?: string }) {
  return (
    <Group gap="sm" mb="md">
      <ThemeIcon size={32} radius="md" variant="light" color="gray">
        {icon}
      </ThemeIcon>
      <Box>
        <Text fw={600} size="sm">
          {label}
        </Text>
        {description && (
          <Text size="xs" c="dimmed">
            {description}
          </Text>
        )}
      </Box>
    </Group>
  );
}

export function AdvancedTokensForm({ values, onChange, suggestedColors = [] }: Props) {
  return (
    <Box>
      {/* Layout Style */}
      <Paper p="lg" radius="md" withBorder mb="md">
        <SectionHeader icon={<IconLayoutSidebar size={18} />} label="Layout хэв маяг" description="Хажуугийн цэсний болон header-ийн ерөнхий зохион байгуулалт" />
        <SegmentedControl
          fullWidth
          value={values.layoutStyle || "wide"}
          onChange={(v) => onChange("layoutStyle", v)}
          data={[
            { label: "Wide (өргөн, full-height sidebar)", value: "wide" },
            { label: "Compact (нарийн, icon top)", value: "compact" },
          ]}
        />
        <Text size="xs" c="dimmed" mt="xs">
          Wide: Logo + модулийн сонголт sidebar-ийн дээд талд, icon-left + text-right NavItem. Compact: Logo + модулийн сонголт header дээр, 80px sidebar, icon-top + text-below.
        </Text>
      </Paper>

      {/* General Settings */}
      <Paper p="lg" radius="md" withBorder mb="md">
        <SectionHeader icon={<IconSettings size={18} />} label="Ерөнхий тохиргоо" description="Фонт, хэмжээ, булан радиус, зай" />
        <Grid>
          <Grid.Col span={4}>
            <Select label="Фонт" data={fontFamilyOptions} value={values.fontFamily || "Inter, sans-serif"} onChange={(v) => onChange("fontFamily", v)} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput label="Фонт хэмжээ (px)" value={values.fontSize || 14} onChange={(v) => onChange("fontSize", v)} min={10} max={20} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput label="Булан радиус (px)" value={values.borderRadius || 4} onChange={(v) => onChange("borderRadius", v)} min={0} max={20} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput label="Зай (px)" value={values.spacing || 8} onChange={(v) => onChange("spacing", v)} min={2} max={24} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput label="Header өндөр (px)" value={values.headerHeight || 60} onChange={(v) => onChange("headerHeight", v)} min={40} max={120} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput label="Navbar өргөн (px)" value={values.navbarWidth || 120} onChange={(v) => onChange("navbarWidth", v)} min={60} max={300} />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Header */}
      <Paper p="lg" radius="md" withBorder mb="md">
        <SectionHeader icon={<IconLayout size={18} />} label="Header" description="Дээд хэсгийн дэвсгэр, текст, хүрээ өнгө" />
        <Grid>
          <Grid.Col span={6}>
            <ColorPickerRow label="Дэвсгэр" value={values.headerBg || ""} onChange={(v) => onChange("headerBg", v)} suggestedColors={suggestedColors} />
          </Grid.Col>
          <Grid.Col span={6}>
            <ColorPickerRow label="Текст" value={values.headerText || ""} onChange={(v) => onChange("headerText", v)} />
          </Grid.Col>
          <Grid.Col span={6}>
            <ColorPickerRow label="Доод хүрээ" value={values.headerBorder || ""} onChange={(v) => onChange("headerBorder", v)} suggestedColors={suggestedColors} />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Navbar */}
      <Paper p="lg" radius="md" withBorder mb="md">
        <SectionHeader icon={<IconSquareRoundedLetterN size={18} />} label="Navbar" description="Хажуугийн цэсний өнгө тохиргоо" />
        <Grid>
          <Grid.Col span={6}>
            <ColorPickerRow label="Дэвсгэр" value={values.navbarBg || ""} onChange={(v) => onChange("navbarBg", v)} />
          </Grid.Col>
          <Grid.Col span={6}>
            <ColorPickerRow label="Текст" value={values.navbarText || ""} onChange={(v) => onChange("navbarText", v)} />
          </Grid.Col>
          <Grid.Col span={6}>
            <ColorPickerRow label="Идэвхтэй дэвсгэр" value={values.navbarActiveBg || ""} onChange={(v) => onChange("navbarActiveBg", v)} suggestedColors={suggestedColors} />
          </Grid.Col>
          <Grid.Col span={6}>
            <ColorPickerRow label="Идэвхтэй текст" value={values.navbarActiveText || ""} onChange={(v) => onChange("navbarActiveText", v)} />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Button */}
      <Paper p="lg" radius="md" withBorder mb="md">
        <SectionHeader icon={<IconClick size={18} />} label="Товч" description="Товчны дэвсгэр, текст, hover өнгө" />
        <Grid>
          <Grid.Col span={4}>
            <ColorPickerRow label="Дэвсгэр" value={values.buttonBg || ""} onChange={(v) => onChange("buttonBg", v)} suggestedColors={suggestedColors} />
          </Grid.Col>
          <Grid.Col span={4}>
            <ColorPickerRow label="Текст" value={values.buttonText || ""} onChange={(v) => onChange("buttonText", v)} />
          </Grid.Col>
          <Grid.Col span={4}>
            <ColorPickerRow label="Hover дэвсгэр" value={values.buttonHoverBg || ""} onChange={(v) => onChange("buttonHoverBg", v)} suggestedColors={suggestedColors} />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Page & Card */}
      <Paper p="lg" radius="md" withBorder mb="md">
        <SectionHeader icon={<IconBrush size={18} />} label="Хуудас, карт" description="Хуудасны дэвсгэр, картны дэвсгэр, хүрээ" />
        <Grid>
          <Grid.Col span={4}>
            <ColorPickerRow label="Хуудасны дэвсгэр" value={values.bodyBg || ""} onChange={(v) => onChange("bodyBg", v)} />
          </Grid.Col>
          <Grid.Col span={4}>
            <ColorPickerRow label="Картны дэвсгэр" value={values.cardBg || ""} onChange={(v) => onChange("cardBg", v)} />
          </Grid.Col>
          <Grid.Col span={4}>
            <ColorPickerRow label="Картны хүрээ" value={values.cardBorder || ""} onChange={(v) => onChange("cardBorder", v)} />
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Semantic & Link */}
      <Paper p="lg" radius="md" withBorder>
        <SectionHeader icon={<IconPalette size={18} />} label="Семантик өнгө" description="Амжилт, анхааруулга, алдаа, холбоос" />
        <Grid>
          <Grid.Col span={3}>
            <ColorPickerRow label="Амжилттай" value={values.successColor || "#40c057"} onChange={(v) => onChange("successColor", v)} />
          </Grid.Col>
          <Grid.Col span={3}>
            <ColorPickerRow label="Анхааруулга" value={values.warningColor || "#fab005"} onChange={(v) => onChange("warningColor", v)} />
          </Grid.Col>
          <Grid.Col span={3}>
            <ColorPickerRow label="Алдаа" value={values.errorColor || "#fa5252"} onChange={(v) => onChange("errorColor", v)} />
          </Grid.Col>
          <Grid.Col span={3}>
            <ColorPickerRow label="Холбоос" value={values.linkColor || ""} onChange={(v) => onChange("linkColor", v)} suggestedColors={suggestedColors} />
          </Grid.Col>
        </Grid>
      </Paper>
    </Box>
  );
}
